(function () {
  var ROOT_SELECTOR = '[data-partner-storefront-dashboard]';
  var FILTER_SELECTOR = '[data-partner-category-filter]';
  var CARD_SELECTOR = '[data-partner-product-card]';
  var ROW_SELECTOR = '[data-partner-storefront-row]';
  var ROW_SCROLLER_SELECTOR = '[data-partner-row-scroller]';
  var ROW_PREV_SELECTOR = '[data-partner-row-prev]';
  var ROW_NEXT_SELECTOR = '[data-partner-row-next]';
  var ROW_CONTROLS_SELECTOR = '[data-partner-row-controls]';
  var NAV_LINK_SELECTOR = '[data-partner-storefront-navigation] a[href^="#partner-row-"]';
  var ACTIVE_CLASS = 'is-active';
  var OVERFLOW_CLASS = 'has-overflow';
  var AT_START_CLASS = 'is-at-start';
  var AT_END_CLASS = 'is-at-end';
  var DRAGGING_CLASS = 'is-dragging';
  var DRAG_THRESHOLD = 6;

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function setFilter(root, category) {
    var normalizedCategory = category || 'all';
    var cards = Array.prototype.slice.call(root.querySelectorAll(CARD_SELECTOR));

    cards.forEach(function (card) {
      var categories = String(card.getAttribute('data-partner-categories') || '').split(/\s+/);
      var shouldShow = normalizedCategory === 'all' || categories.indexOf(normalizedCategory) !== -1;
      card.hidden = !shouldShow;
    });

    root.querySelectorAll(FILTER_SELECTOR).forEach(function (button) {
      var isActive = button.getAttribute('data-partner-category-filter') === normalizedCategory;
      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function getScrollStep(scroller) {
    var firstItem = scroller && scroller.querySelector('.grid-item');
    if (!firstItem) return scroller ? Math.max(scroller.clientWidth * 0.8, 1) : 1;

    var itemRect = firstItem.getBoundingClientRect();
    var styles = window.getComputedStyle(scroller.querySelector('.partner-storefront-row__items') || scroller);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return Math.max(itemRect.width + gap, 1);
  }

  function snapToNearest(scroller) {
    var step = getScrollStep(scroller);
    var maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    var target = Math.round(scroller.scrollLeft / step) * step;
    target = Math.min(Math.max(target, 0), maxScroll);
    if (Math.abs(maxScroll - scroller.scrollLeft) < Math.abs(target - scroller.scrollLeft)) {
      target = maxScroll;
    }
    scroller.scrollTo({ left: target, behavior: 'smooth' });
  }

  function updateRowState(row) {
    var scroller = row.querySelector(ROW_SCROLLER_SELECTOR);
    var controls = row.querySelector(ROW_CONTROLS_SELECTOR);
    var prev = row.querySelector(ROW_PREV_SELECTOR);
    var next = row.querySelector(ROW_NEXT_SELECTOR);
    if (!scroller) return;

    var hasOverflow = scroller.scrollWidth > scroller.clientWidth + 2;
    row.classList.toggle(OVERFLOW_CLASS, hasOverflow);
    if (controls) controls.hidden = !hasOverflow;

    var maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    var scrollLeft = Math.max(scroller.scrollLeft, 0);
    var isAtStart = !hasOverflow || scrollLeft <= 2;
    var isAtEnd = !hasOverflow || scrollLeft >= maxScroll - 2;

    row.classList.toggle(AT_START_CLASS, isAtStart);
    row.classList.toggle(AT_END_CLASS, isAtEnd);
    if (prev) prev.disabled = isAtStart;
    if (next) next.disabled = isAtEnd;
  }

  function scrollRow(row, direction) {
    var scroller = row.querySelector(ROW_SCROLLER_SELECTOR);
    if (!scroller) return;

    var step = getScrollStep(scroller);
    var target = scroller.scrollLeft + step * direction;
    scroller.scrollTo({ left: target, behavior: 'smooth' });
  }

  function getHeaderOffset() {
    var styles = window.getComputedStyle(document.body);
    var headerHeight = parseFloat(styles.getPropertyValue('--header-height') || '0') || 0;
    return headerHeight + (window.matchMedia('(max-width: 767px)').matches ? 16 : 24);
  }

  function scrollToRow(target, updateHash) {
    if (!target) return;
    var top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
    if (updateHash && window.history && window.history.pushState) {
      window.history.pushState(null, '', '#' + target.id);
    }
  }

  function initRow(row) {
    if (!row || row.dataset.partnerRowReady === 'true') return;
    row.dataset.partnerRowReady = 'true';

    var scroller = row.querySelector(ROW_SCROLLER_SELECTOR);
    var prev = row.querySelector(ROW_PREV_SELECTOR);
    var next = row.querySelector(ROW_NEXT_SELECTOR);
    if (!scroller) return;

    var scrollFrame = null;
    var drag = {
      active: false,
      moved: false,
      suppressClick: false,
      startX: 0,
      startScrollLeft: 0,
      pointerId: null
    };

    function requestStateUpdate() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(function () {
        scrollFrame = null;
        updateRowState(row);
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        scrollRow(row, -1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        scrollRow(row, 1);
      });
    }

    scroller.addEventListener('scroll', requestStateUpdate, { passive: true });

    scroller.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      drag.active = true;
      drag.moved = false;
      drag.suppressClick = false;
      drag.startX = event.clientX;
      drag.startScrollLeft = scroller.scrollLeft;
      drag.pointerId = event.pointerId;
    });

    scroller.addEventListener('pointermove', function (event) {
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      var distance = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

      if (!drag.moved) {
        drag.moved = true;
        drag.suppressClick = true;
        row.classList.add(DRAGGING_CLASS);
        scroller.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
      scroller.scrollLeft = drag.startScrollLeft - distance;
    });

    scroller.addEventListener('pointerup', function (event) {
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      drag.active = false;
      row.classList.remove(DRAGGING_CLASS);
      if (drag.moved) snapToNearest(scroller);
      drag.moved = false;
      drag.pointerId = null;
      window.setTimeout(function () {
        drag.suppressClick = false;
      }, 0);
      requestStateUpdate();
    });

    scroller.addEventListener('pointercancel', function () {
      if (!drag.active) return;
      drag.active = false;
      drag.moved = false;
      drag.suppressClick = false;
      drag.pointerId = null;
      row.classList.remove(DRAGGING_CLASS);
      requestStateUpdate();
    });

    scroller.addEventListener('click', function (event) {
      if (!drag.suppressClick) return;
      drag.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    window.addEventListener('resize', requestStateUpdate);
    updateRowState(row);
  }

  function initDashboard(root) {
    if (!root || root.dataset.partnerDashboardReady === 'true') return;
    root.dataset.partnerDashboardReady = 'true';

    root.addEventListener('click', function (event) {
      var navigationLink = event.target && event.target.closest && event.target.closest(NAV_LINK_SELECTOR);
      if (navigationLink && root.contains(navigationLink)) {
        var targetId = navigationLink.getAttribute('href').slice(1);
        var target = document.getElementById(targetId);
        if (target) {
          event.preventDefault();
          scrollToRow(target, true);
          return;
        }
      }

      var button = event.target && event.target.closest && event.target.closest(FILTER_SELECTOR);
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      setFilter(root, button.getAttribute('data-partner-category-filter'));
    });

    root.querySelectorAll(ROW_SELECTOR).forEach(initRow);
  }

  ready(function () {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initDashboard);
  });
})();
